import { apiClient } from './client'

interface Address {
  numberAndStreet?: string
  city?: string
  province?: string
  postalCode?: string
  country?: string
}
export type SwtchAddress = Address

interface GoogleGeocodeLatLng {
  lat: number
  lng: number
}

interface GoogleGeocodeGeometry {
  location: GoogleGeocodeLatLng
}

interface GoogleGeocodeAddressComponent {
  long_name: string
  short_name: string
  types: string[]
}

interface GoogleGeocodeResult {
  geometry: GoogleGeocodeGeometry
  address_components: GoogleGeocodeAddressComponent[]
  formatted_address: string
  partial_match: boolean
  place_id: string
  types: string[]
}

interface GoogleGeocodeResponse {
  results: GoogleGeocodeResult[]
  status: string
  error_message?: string
}

const buildBackendGeocodePath = (params: { address?: string; lat?: number; lng?: number }) => {
  if (params.address != null) {
    return `/google/geocode?address=${encodeURIComponent(params.address)}`
  }
  if (params.lat != null && params.lng != null) {
    return `/google/reverse_geocode?lat=${params.lat}&lng=${params.lng}`
  }
  return ''
}

export type GeocodingSuccess = {
  found: true
  lat: number
  lng: number
  numberAndStreet?: string
  address?: string
  province?: string
  country?: string
  city?: string
  postalCode?: string
}

export type GeocodingFailure = {
  found: false
  code: string
  message: string
}

export type GeocodingResult = GeocodingSuccess | GeocodingFailure

const buildAddressString = (address: SwtchAddress): string => {
  const addressComponents: string[] = []
  if (address.numberAndStreet) addressComponents.push(address.numberAndStreet)
  if (address.city) addressComponents.push(address.city)
  if (address.province) addressComponents.push(address.province)
  if (address.postalCode) addressComponents.push(address.postalCode)
  if (address.country) addressComponents.push(address.country)
  return addressComponents.join(', ')
}

const getLocationFromAddress = async (address: SwtchAddress): Promise<GeocodingResult> => {
  const path = buildBackendGeocodePath({ address: buildAddressString(address) })
  const response = await callBackendGeocode(path)
  return handleGeocodingResponse(response, address)
}

const callBackendGeocode = async (path: string): Promise<GoogleGeocodeResponse> => {
  try {
    return await apiClient<GoogleGeocodeResponse>(path, {})
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.'
    return { results: [], status: 'REQUEST_FAILED', error_message: errorMessage }
  }
}

// Helper functions - defined before selectBestGeocodeResult
const extractAddressComponent = (result: GoogleGeocodeResult, type: string): string | undefined => {
  return result.address_components.find((ac) => ac.types.includes(type))?.short_name
}

const extractCity = (result: GoogleGeocodeResult): string | undefined => {
  return (
    result.address_components.find((ac) => ac.types.includes('locality'))?.short_name ||
    result.address_components.find((ac) => ac.types.includes('neighborhood'))?.short_name ||
    result.address_components.find((ac) => ac.types.includes('sublocality'))?.short_name ||
    result.address_components.find((ac) => ac.types.includes('postal_town'))?.short_name
  )
}

const selectBestGeocodeResult = (response: GoogleGeocodeResponse, address: SwtchAddress) => {
  if (response.results.length === 0) {
    return null
  }
  
  if (response.results.length === 1) {
    return response.results[0]
  }
  
  // If we have a country preference, prefer results matching that country
  if (address.country) {
    const countryCode = address.country.toUpperCase()
    // Normalize country codes (US, USA, United States all map to US)
    const normalizedCountry = countryCode === 'USA' || countryCode === 'UNITED STATES' ? 'US' : countryCode
    
    const countryMatch = response.results.find((r) => {
      const resultCountry = extractAddressComponent(r, 'country')
      if (!resultCountry) return false
      const normalizedResultCountry = resultCountry.toUpperCase()
      return normalizedResultCountry === normalizedCountry || 
             normalizedResultCountry === countryCode ||
             (normalizedCountry === 'US' && (normalizedResultCountry === 'USA' || normalizedResultCountry === 'UNITED STATES'))
    })
    if (countryMatch) {
      return countryMatch
    }
  }
  
  // If we have a province/state preference, prefer results matching that
  if (address.province) {
    const provinceMatch = response.results.find((r) => {
      const resultProvince = extractAddressComponent(r, 'administrative_area_level_1')
      return resultProvince?.toUpperCase() === address.province?.toUpperCase()
    })
    if (provinceMatch) {
      return provinceMatch
    }
  }
  
  // If we have a city preference, prefer results matching that
  if (address.city) {
    const cityMatch = response.results.find((r) => {
      const resultCity = extractCity(r)
      return resultCity?.toLowerCase() === address.city?.toLowerCase()
    })
    if (cityMatch) {
      return cityMatch
    }
  }
  
  // Fallback: prefer results that include the street address
  const streetMatch = response.results.find((r) => 
    r.formatted_address.includes(address.numberAndStreet ?? '')
  )
  if (streetMatch) {
    return streetMatch
  }
  
  // Default to first result
  return response.results[0]
}

const formatNumberAndStreet = (result: GoogleGeocodeResult, country?: string) => {
  if (country === 'United States' || country === 'Canada') {
    return `${extractAddressComponent(result, 'street_number') || ''} ${
      extractAddressComponent(result, 'route') || ''
    }`.trim()
  }
  return result.formatted_address
    .replace(new RegExp(`, ${extractAddressComponent(result, 'postal_code')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'sublocality')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'locality')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'country')}`), '')
    .replace(new RegExp(`, ${extractAddressComponent(result, 'administrative_area_level_1')}`), '')
    .replace(/,\s*$/, '')
}

const handleGeocodingResponse = (response: GoogleGeocodeResponse, address: SwtchAddress): GeocodingResult => {
  if (!response.results || response.results.length === 0) {
    return { found: false, message: 'No results found.', code: response.status }
  }

  let result = selectBestGeocodeResult(response, address)
  
  if (!result) {
    return { found: false, message: 'No valid result found.', code: response.status }
  }
  const { lat, lng } = result.geometry.location

  let country = extractAddressComponent(result, 'country')
  let province = extractAddressComponent(result, 'administrative_area_level_1')

  const numberAndStreet = formatNumberAndStreet(result, country)

  // Handle Puerto Rico case
  if (country === 'Puerto Rico' || country === 'PR') {
    country = 'US'
    province = 'PR'
  }

  return {
    found: true,
    lat,
    lng,
    numberAndStreet,
    city: extractCity(result),
    postalCode: extractAddressComponent(result, 'postal_code'),
    country,
    province,
  }
}

const geocodeResponseToResult = (res: GoogleGeocodeResponse): GeocodingResult => {
  if (res.results.length === 0) {
    return {
      found: false,
      code: res.status,
      message: res.error_message || 'Google says no',
    }
  }
  const latlng = res.results[0].geometry.location
  return {
    found: true,
    ...latlng,
    province: res.results[0].address_components.find((ac) => ac.types.includes('administrative_area_level_1'))
      ?.short_name,
    country: res.results[0].address_components.find((ac) => ac.types.includes('country'))?.short_name,
  }
}

const geocode = async (address: SwtchAddress): Promise<GeocodingResult> => {
  const addressStr = buildAddressString(address)
  const path = buildBackendGeocodePath({ address: addressStr })
  const res = await callBackendGeocode(path)
  return geocodeResponseToResult(res)
}

const reverseGeocode = async (latlng: { lat: number; lng: number }): Promise<GeocodingResult> => {
  const path = buildBackendGeocodePath({ lat: latlng.lat, lng: latlng.lng })
  const res = await callBackendGeocode(path)
  return geocodeResponseToResult(res)
}

export { geocode, reverseGeocode, getLocationFromAddress }
